import "./style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { CircularProgress, Tooltip } from "@mui/material";
import ActionSend from "assets/img/svg/actionSend";
import Downloadnew from "assets/img/svg/downloadnew";
import SendEmailIcon from "assets/img/svg/sendEmail";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { GeneralTabTerm, ITerm } from "store/managementStore";
import { setLastSentAtTerms } from "store/managementStore";
import {
    fetchAndSaveStatement,
    fetchCollectiveManagementStatement,
    fetchNetStatement,
    fetchPrecountStatement,
    fetchStatement,
    resetStatements,
    sendMailWithStatement,
} from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import type { EStatementType } from "types";
import { PeriodStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import { formatLocaleDateString, getStatementFilename } from "../../../helpers/utils";
import ButtonBase from "../ButtonBase";
import EmptyState from "../EmptyState";
import SendEmail from "../SendEmail";
import { Spinner } from "../Spinner";

type TProps = {
    term: ITerm | GeneralTabTerm;
    companyCurrency?: string;
    isStatementCurrencyDefault: boolean;
    grossStatement: string;
    activePeriod: number | undefined;
};

const statementType = [
    { labelKey: "Summary.StatementOfGrossDuties", type: "gross" },
    { labelKey: "Summary.StatementOfNetDuties", type: "net" },
    { labelKey: "Summary.PrepaymentCertification", type: "precount" },
    { labelKey: "Summary.StatementOfCollectiveManagement", type: "collectiveManagement" },
];

/**
 * Title statement that can be accessed from Management > General (specific to one term)
 */
function TitleStatement({
    term,
    isStatementCurrencyDefault = true,
    grossStatement,
    activePeriod,
    companyCurrency,
}: TProps) {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { statementsSettings } = useSelector((state: RootState) => state.settings);
    const periodStatus = useSelector((state: RootState) => state.period.activePeriodData?.isActive) as
        | PeriodStatus
        | undefined;
    const isPeriodFuture = periodStatus === PeriodStatus.FUTURE;
    const { status, netStatement, precountStatement, collectiveManagementStatement, savingStatus } = useSelector(
        (state: RootState) => state.statements,
    );
    const hasCollectiveManagement =
        term.hasPostCloseCollectiveManagementSales > 0 && periodStatus === PeriodStatus.CLOSED;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const hasAuthorPortalAccess = term.accountStatus && term.accountStatus == "confirmed";
    const isPrecounted = term.precount == "1" || (term.precount == "2" && (term.contributions ?? 0) > 0);
    const sendTermText = hasAuthorPortalAccess ? t("Summary.SendToBeneficiarPortal") : t("Summary.SendToBeneficiar");
    const [currentGrossStatement, setCurrentGrossStatement] = useState(grossStatement);
    const [isTooltipDisabled, setIsTooltipDisabled] = useState(true);

    const { getCompanySettings, countryFeatures } = useCompanySettings();
    const usesMarketSegmentation = getCompanySettings("uses_market_segmentation");
    const { showNet, showPrecount } = countryFeatures.statementBlocks;

    useEffect(() => {
        if (activePeriod) {
            currentTab == 0 &&
                !grossStatement &&
                dispatch(fetchStatement({ termId: term.id, periodId: activePeriod })).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setCurrentGrossStatement(resp.payload);
                    }
                });
            currentTab == 1 &&
                !netStatement &&
                dispatch(fetchNetStatement({ termId: term.id, periodId: activePeriod }));
            currentTab == 2 &&
                !precountStatement &&
                dispatch(fetchPrecountStatement({ termId: term.id, periodId: activePeriod }));
            currentTab == 3 &&
                !collectiveManagementStatement &&
                dispatch(fetchCollectiveManagementStatement({ termId: term.id, periodId: activePeriod }));
        }
    }, [activePeriod, term.id, currentTab]);

    useEffect(() => {
        return () => {
            dispatch(resetStatements());
        };
    }, []);

    const savePdf = useCallback(() => {
        if (savingStatus == "pending") return;
        if (activePeriod) {
            dispatch(
                fetchAndSaveStatement({
                    termId: term.id,
                    periodId: activePeriod,
                    fileName: getStatementFilename(
                        term.author,
                        term.title,
                        t(statementType[currentTab].labelKey),
                        new Date(),
                    ),
                    statementType: statementType[currentTab].type,
                }),
            );
        }
    }, [activePeriod, dispatch, term.id, savingStatus, currentTab]);

    const sendEmail = (emails: string[], subject: string, body: string, ignore?: EStatementType[]) => {
        if (!activePeriod) return;
        dispatch(
            sendMailWithStatement({
                termId: term.id,
                periodId: activePeriod,
                mailCc: emails,
                subject,
                body,
                ignore,
            }),
        );
        dispatch(setLastSentAtTerms([term.id]));
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (currentGrossStatement) {
            const cumulativeSales = document.getElementById("cumulative-sales");
            const physicalChannels = document.getElementById("physical-channels");
            const digitalChannels = document.getElementById("digital-channels");
            const rightTransferChannels = document.getElementById("rights-transfer-channels");
            const socialOrgsChannels = document.getElementById("social-orgs-channels");
            const stocks = document.getElementById("stocks");
            if (cumulativeSales && !statementsSettings.cumulativeSales) {
                cumulativeSales.remove();
            }
            if (stocks && !statementsSettings.stocks) {
                stocks.remove();
            }

            if (physicalChannels && !statementsSettings.physicalChannels) {
                physicalChannels.remove();
            }
            if (digitalChannels && !statementsSettings.digitalChannels) {
                digitalChannels.remove();
            }
            if (rightTransferChannels && !statementsSettings.rightTransferChannels) {
                rightTransferChannels.remove();
            }
            if (socialOrgsChannels && !statementsSettings.socialOrgsChannels) {
                socialOrgsChannels.remove();
            }
        }
    }, [currentGrossStatement]);

    useEffect(() => {
        setIsTooltipDisabled(isStatementCurrencyDefault);
    }, [isStatementCurrencyDefault]);

    return (
        <>
            {isModalOpen && (
                <SendEmail
                    isModalOpen={isModalOpen}
                    termData={{
                        beneficiaryId: term?.beneficiarId,
                        author: term?.author,
                        title: term?.title,
                        email: term?.email ?? "",
                        precount: `${term?.precount}`,
                        contributions: term?.contributions,
                    }}
                    onClose={() => setIsModalOpen(false)}
                    sendEmail={sendEmail}
                    hasAuthorPortalAccess={!!(term.accountStatus && term.accountStatus == "confirmed")}
                    isStatementCurrencyDefault={isStatementCurrencyDefault}
                    hasCollectiveManagement={hasCollectiveManagement}
                />
            )}
            <div className="summary-statements-header">
                <div className="summary-statements-buttons-list">
                    <div
                        className={`summary-statements-button${(currentTab == 0 && " active") || ""}`}
                        onClick={() => setCurrentTab(0)}
                    >
                        {t("Summary.StatementOfGrossDuties")}
                    </div>
                    {showNet && (
                        <Tooltip
                            title={isTooltipDisabled ? "" : t("Summary.NoNet", { companyCurrency: companyCurrency })}
                        >
                            <div
                                className={`summary-statements-button${(currentTab == 1 && " active") || ""} ${
                                    !isStatementCurrencyDefault ? "disabled" : ""
                                }`}
                                onClick={() => isStatementCurrencyDefault && setCurrentTab(1)}
                            >
                                {t("Summary.StatementOfNetDuties")}
                            </div>
                        </Tooltip>
                    )}
                    {showPrecount && (
                        <Tooltip
                            title={
                                !isPrecounted
                                    ? t("Summary.NoPrecount")
                                    : !isStatementCurrencyDefault
                                      ? t("Summary.NoPrecount2", { companyCurrency: companyCurrency })
                                      : ""
                            }
                        >
                            <div
                                className={`summary-statements-button${(currentTab == 2 && " active") || ""}${
                                    !isPrecounted || !isStatementCurrencyDefault ? " disabled" : ""
                                }`}
                                onClick={() => isPrecounted && setCurrentTab(2)}
                            >
                                {t("Summary.PrepaymentCertification")}
                            </div>
                        </Tooltip>
                    )}
                    {hasCollectiveManagement && (
                        <div
                            className={`summary-statements-button${(currentTab == 3 && " active") || ""}`}
                            onClick={() => setCurrentTab(3)}
                        >
                            {t("Summary.StatementOfCollectiveManagement")}
                        </div>
                    )}
                </div>
                {term.lastSentAt && (
                    <div className="summary-statements-lastSentAt">
                        {hasAuthorPortalAccess ? t("Management.SentToPortalAt") : t("Management.MailSentAt")}{" "}
                        {formatLocaleDateString({ date: term.lastSentAt, options: { numeric: true, timeZone: "UTC" } })}
                        <div className="badge"></div>
                    </div>
                )}
            </div>
            <div className="summary-show-statements-header">
                <div className="summary-show-statements-current-currentGrossStatement">
                    {t(statementType[currentTab].labelKey)}
                </div>
                <div className="summary-show-statements-header-actions">
                    {/* <div className="summary-show-statements-header-button"><Printer /></div> */}
                    <div className="summary-show-statements-header-button" onClick={savePdf}>
                        {(savingStatus == "pending" && (
                            <CircularProgress size={20} sx={{ color: variables["primary-purple"] }} />
                        )) || <Downloadnew />}
                    </div>
                    {!isPeriodFuture && (
                        <div className="summary-show-statements-header-buttonBase">
                            <ButtonBase
                                styles={{ borderRadius: "5px" }}
                                variant="contained"
                                disabled={!term.email || (!!hasAuthorPortalAccess && !!term.lastSentAt)}
                                onClick={() => setIsModalOpen(true)}
                            >
                                {term.email
                                    ? hasAuthorPortalAccess && term.lastSentAt
                                        ? t("SendEmail.AlreadyTransmit")
                                        : sendTermText
                                    : t("Summary.NoEmail")}
                                {hasAuthorPortalAccess ? <ActionSend /> : <SendEmailIcon />}
                            </ButtonBase>
                        </div>
                    )}
                </div>
            </div>
            <TabContext value={currentTab.toString()}>
                <TabPanel sx={{ padding: 0 }} value="0">
                    {status === "pending" && (
                        <div className="spinner-wrapper">
                            <Spinner />
                            <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                        </div>
                    )}
                    {status === "failed" && (
                        <div className="spinner-wrapper">
                            {usesMarketSegmentation ? (
                                <div className="no-statement-info">
                                    <span className="message">{t("Summary.MarketSegmentationInfo")}</span>
                                </div>
                            ) : (
                                <EmptyState type="statementFailed" />
                            )}
                        </div>
                    )}
                    {status === "succeeded" && (
                        <div
                            style={{
                                maxWidth: "960px",
                                border: "39px solid #c8c8c852",
                                paddingBottom: 24,
                            }}
                            dangerouslySetInnerHTML={{ __html: grossStatement }}
                        ></div>
                    )}
                </TabPanel>
                {showNet && (
                    <TabPanel sx={{ padding: 0 }} value="1">
                        {status === "pending" && (
                            <div className="spinner-wrapper">
                                <Spinner />
                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                            </div>
                        )}
                        {status === "failed" && (
                            <div className="spinner-wrapper">
                                {usesMarketSegmentation ? (
                                    <div className="no-statement-info">
                                        <span className="message">{t("Summary.MarketSegmentationInfo")}</span>
                                    </div>
                                ) : (
                                    <EmptyState type="statementFailed" />
                                )}
                            </div>
                        )}
                        {status === "succeeded" && (
                            <div
                                style={{
                                    maxWidth: "960px",
                                    border: "39px solid #c8c8c852",
                                    paddingBottom: 24,
                                }}
                                dangerouslySetInnerHTML={{ __html: netStatement }}
                            ></div>
                        )}
                    </TabPanel>
                )}
                {showPrecount && (
                    <TabPanel sx={{ padding: 0 }} value="2">
                        {status === "pending" && (
                            <div className="spinner-wrapper">
                                <Spinner />
                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                            </div>
                        )}
                        {status === "failed" && (
                            <div className="spinner-wrapper">
                                {usesMarketSegmentation ? (
                                    <div className="no-statement-info">
                                        <span className="message">{t("Summary.MarketSegmentationInfoPrecount")}</span>
                                    </div>
                                ) : (
                                    <EmptyState type="statementFailed" />
                                )}
                            </div>
                        )}
                        {status === "succeeded" && (
                            <div
                                style={{
                                    maxWidth: "960px",
                                    border: "39px solid #c8c8c852",
                                    paddingBottom: 24,
                                }}
                                dangerouslySetInnerHTML={{ __html: precountStatement }}
                            ></div>
                        )}
                    </TabPanel>
                )}
                {hasCollectiveManagement && (
                    <TabPanel sx={{ padding: 0 }} value="3">
                        {status === "pending" && (
                            <div className="spinner-wrapper">
                                <Spinner />
                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                            </div>
                        )}
                        {status === "failed" && (
                            <div className="spinner-wrapper">
                                <EmptyState type="statementFailed" />
                            </div>
                        )}
                        {status === "succeeded" && (
                            <div
                                style={{
                                    maxWidth: "960px",
                                    border: "39px solid #c8c8c852",
                                    paddingBottom: 24,
                                }}
                                dangerouslySetInnerHTML={{ __html: collectiveManagementStatement }}
                            ></div>
                        )}
                    </TabPanel>
                )}
            </TabContext>
        </>
    );
}

export default TitleStatement;
